import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

import { forceControlOffScope } from './shared/forceControlOffScope';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['signFormAddressPlaceholderExperiment'];

type Params = {
	experiment: Experiment;
	fullAddressCollectionEnabled: boolean;
};

/**
 * Treatment is deferred to the front-end (`useTreatAddressPlaceholderExperiment`), so `treated` is always
 * forced to `false` here — the middle-end can return `treated: true` for a returning session even with
 * `treat: false`.
 *
 * Deferral is what scopes the analysis to sessions that actually reached the address step. The primary
 * metric is `sign_post` per `sign_intent_continue`, so the treated population has to be visitors who
 * tapped Continue on step 1 — known only at render time. Treating here would enroll every petition-details
 * pageview instead, padding the denominator with visitors who never opened the sign form.
 */
export function normalizeSignFormAddressPlaceholderExperiment({
	experiment,
	fullAddressCollectionEnabled,
}: Params): Experiment {
	if (!shouldRunExperiment({ experiment, fullAddressCollectionEnabled })) {
		return forceControlOffScope(experiment);
	}

	return { ...experiment, treated: false };
}

/**
 * `fullAddressCollectionEnabled` is the repo's equivalent of the brief's `sign_address_fields =
 * full_address`: it is the single condition under which the Google-autocomplete street-address field
 * renders at all (see `isFullAddressCollectionEnabled`). Reused rather than re-derived so this can never
 * disagree with what the form actually renders; the city_only / postal_code_only / prefilled flows fall
 * out of scope for free.
 *
 * No locale or country check here: the experiment's en-US targeting is configured on the
 * `sign_form_address_placeholder` FCM, and anything it excludes arrives with `fallback: true`.
 *
 * That targeting has to constrain locale *and* country. `countryCode` and `locale` are independent
 * attributes (see docs/packages/experiments/growthbook.md), so enabling the FCM for the US alone would
 * still admit a US visitor browsing in another locale — who would then be shown the English example
 * string, since placeholder copy applies to every locale.
 */
function shouldRunExperiment({ experiment, fullAddressCollectionEnabled }: Params): boolean {
	return hasRealAssignment(experiment) && fullAddressCollectionEnabled;
}

/**
 * Any `fallback` means the variation did not come from a real experiment-service assignment — the FCM kill
 * switch (`disabled`, including the per-country targeting above), an unreachable service (`fetch-error`),
 * or an experiment not yet registered (`not-found`). All of them are out of scope, not just the kill
 * switch.
 */
function hasRealAssignment(experiment: Experiment): boolean {
	return !experiment.fallback;
}
